import React from 'react';
import { Paper, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { TooltipProps } from 'recharts';
import type { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { ITaperingStep, IStepMedication, OTPStatus } from '../../../global/entities/OTP/OTP';
import { IDoseSuggestionResult } from '../../../global/entities/MME/MME';
import { addCalendarDays, toLocalCalendarDate } from '../../../global/helpers/isoCalendarDate';

// --- Types ---

export interface ChartDataPoint {
    date: number;
    dateLabel: string;
    mme: number;
    virtualMME?: number;
    actualMME?: number;
    percentage: number;
    step: number;
    upperBound: number;
    lowerBound: number;
}

// --- Pure helpers ---

export const roundToNearestHalf = (value: number): number => Math.round(value * 2) / 2;

export const calculateStepMedicationsMME = (medications: IStepMedication[] | undefined): number | undefined => {
    if (!medications || medications.length === 0) return undefined;

    const hasAllFactors = medications.every((m) => m.conversionFactor !== undefined);
    if (!hasAllFactors) return undefined;

    const totalMME = medications.reduce((sum, med) => {
        const mme = med.dosageStrength * med.frequencyPerDay * (med.conversionFactor || 0);
        return sum + mme;
    }, 0);

    return roundToNearestHalf(totalMME);
};

export const convertTaperingStepsToChartData = (
    steps: ITaperingStep[],
    locale: string,
    doseSuggestions?: IDoseSuggestionResult[],
    virtualMMEArray?: number[],
): ChartDataPoint[] => {
    const dataPoints: ChartDataPoint[] = [];
    const localeTag = locale === 'fr' ? 'fr-CA' : 'en-CA';

    steps.forEach((step, index) => {
        const suggestion = doseSuggestions?.[index];
        const d = toLocalCalendarDate(step.startDate) ?? new Date(NaN);

        const medicationsMME = calculateStepMedicationsMME(step.medications);
        const actualMME = medicationsMME ?? (suggestion?.hasSuggestion ? suggestion.actualMME : undefined);

        const virtual = virtualMMEArray?.[index];
        const hasVirtualDiff = virtual !== undefined && Math.abs(virtual - step.doseMME) >= 0.01;

        dataPoints.push({
            date: d.getTime(),
            dateLabel: d.toLocaleDateString(localeTag, { year: 'numeric', month: 'short', day: 'numeric' }),
            mme: step.doseMME,
            virtualMME: hasVirtualDiff ? virtual : undefined,
            actualMME,
            percentage: step.dosePercentageOfOriginal,
            step: step.stepNumber,
            upperBound: parseFloat((step.doseMME * 1.2).toFixed(2)),
            lowerBound: parseFloat((step.doseMME * 0.8).toFixed(2)),
        });
    });

    return dataPoints;
};

export const formatDateTick = (timestamp: number, locale: string): string => {
    const date = new Date(timestamp);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return locale === 'fr' ? `${dd}/${mm}` : `${mm}/${dd}`;
};

/**
 * Computes virtual MME targets for each step using a multiplicative ratio model.
 * When an HCP manually adjusts medications on a step, the ratio (actual / suggested)
 * propagates forward: virtual = planned * cumulativeRatio.
 *
 * This preserves the reduction rate exactly. With a 25% reduction per step,
 * the virtual also decreases by 25% per step, regardless of how large the adjustment is.
 */
export const computeVirtualMMEArray = (
    steps: ITaperingStep[],
    doseSuggestions?: IDoseSuggestionResult[],
): number[] => {
    let cumulativeRatio = 1.0;
    return steps.map((step, index) => {
        const virtualMME = step.doseMME * cumulativeRatio;

        // Only apply ratio for steps that were manually edited by the user.
        // Auto-populated medications already match the virtual target, so including
        // them would double-count the adjustment after save/reload.
        if (step.isManuallyEdited) {
            const actualMME = calculateStepMedicationsMME(step.medications);
            const suggestedMME = step.suggestedMME ?? doseSuggestions?.[index]?.actualMME;

            if (actualMME !== undefined && suggestedMME !== undefined && suggestedMME > 0)
                cumulativeRatio *= (actualMME / suggestedMME);
        }

        return roundToNearestHalf(virtualMME);
    });
};

/**
 * Adjusts the number of tapering steps so the plan reaches the target final MME
 * after virtual MME from manual edits (ratio model) are applied.
 *
 * When a manual edit pushes the adjusted MME higher, additional steps are appended
 * (continuing the same reduction percentage) until the target is reached.
 * When the target is reached earlier, trailing non-edited steps are removed.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const adjustStepsForVirtualMME = (
    steps: ITaperingStep[],
    originalMME: number,
    reductionPercentage: number,
    daysPerStep: number,
    targetFinalMME: number,
    doseSuggestions?: IDoseSuggestionResult[],
): ITaperingStep[] => {
    if (steps.length === 0 || targetFinalMME <= 0) return steps;

    const virtualMME = computeVirtualMMEArray(steps, doseSuggestions);
    const lastVirtual = virtualMME[virtualMME.length - 1];

    // If the last step's virtual MME already matches the target (within tolerance), no adjustment needed
    if (Math.abs(lastVirtual - targetFinalMME) < 0.5) return steps;

    const reductionFactor = 1 - reductionPercentage / 100;

    if (lastVirtual > targetFinalMME) {
        // Need more steps — continue tapering at the reduction rate.
        // With the ratio model, virtual = planned * cumulativeRatio.
        // New steps have no manual edits, so the ratio stays constant.
        // We taper the virtual directly since virtual = planned * ratio,
        // and planned decreases by reductionFactor each step.
        const result = [...steps];
        const lastStep = steps[steps.length - 1];
        let currentPercent = lastStep.dosePercentageOfOriginal / 100;
        let stepNumber = lastStep.stepNumber + 1;
        let stepStart = addCalendarDays(lastStep.endDate, 1);

        let currentVirtual = lastVirtual;
        while (currentVirtual > targetFinalMME && stepNumber <= 52) {
            currentVirtual = currentVirtual * reductionFactor;
            currentPercent = currentPercent * reductionFactor;

            const doseMME = parseFloat((originalMME * currentPercent).toFixed(2));
            const endDate = addCalendarDays(stepStart, daysPerStep - 1);

            result.push({
                id: 0,
                stepNumber,
                startDate: stepStart,
                endDate,
                durationDays: daysPerStep,
                doseMME,
                dosePercentageOfOriginal: parseFloat((currentPercent * 100).toFixed(1)),
                reductionFromPrevious: parseFloat((result[result.length - 1].doseMME - doseMME).toFixed(2)),
                medications: [],
                customDrugID: lastStep.customDrugID,
                customRouteID: lastStep.customRouteID,
            });

            if (currentVirtual <= targetFinalMME) break;

            stepStart = addCalendarDays(endDate, 1);
            stepNumber++;
        }

        return result;
    } else {
        // Target reached earlier — trim trailing non-edited steps
        // Find the last step where virtual MME is still above target, keep it + one at/below target
        let trimIndex = steps.length;
        for (let i = steps.length - 1; i >= 0; i--) {
            if (steps[i].isManuallyEdited) break; // Never remove manually edited steps
            if (virtualMME[i] <= targetFinalMME && i > 0 && virtualMME[i - 1] > targetFinalMME) {
                trimIndex = i + 1; // Keep this step (it's at/below target)
                break;
            }
            if (virtualMME[i] > targetFinalMME) {
                trimIndex = i + 1;
                break;
            }
        }

        return steps.slice(0, trimIndex);
    }
};

// eslint-disable-next-line react-refresh/only-export-components
export const getVarianceColor = (targetMME: number, actualMME: number): string => {
    const difference = Math.abs(targetMME - actualMME);
    if (difference < 5) return '#4caf50';
    if (difference < 10) return '#ff9800';
    return '#f44336';
};

// eslint-disable-next-line react-refresh/only-export-components
export const getStatusColor = (status: OTPStatus): string => {
    switch (status) {
        case OTPStatus.Draft: return 'gray';
        case OTPStatus.PendingValidation: return 'yellow';
        case OTPStatus.PendingHcpValidation: return 'orange';
        case OTPStatus.Active: return 'green';
        case OTPStatus.Completed: return 'blue';
        case OTPStatus.Aborted: return 'red';
        case OTPStatus.Appended: return 'violet';
        default: return 'gray';
    }
};

// --- React components ---

interface CustomActualMMEDotProps {
    cx?: number;
    cy?: number;
    payload?: ChartDataPoint;
}

export const CustomActualMMEDot: React.FC<CustomActualMMEDotProps> = ({ cx, cy, payload }) => {
    if (cx === undefined || cy === undefined || !payload || payload.actualMME === undefined)
        return null;

    const varianceTarget = payload.virtualMME ?? payload.mme;
    const color = getVarianceColor(varianceTarget, payload.actualMME);

    return (
        <circle
            cx={cx}
            cy={cy}
            r={4}
            fill={color}
            stroke={color}
            strokeWidth={1}
        />
    );
};

export const CustomActualMMEActiveDot: React.FC<CustomActualMMEDotProps> = ({ cx, cy, payload }) => {
    if (cx === undefined || cy === undefined || !payload || payload.actualMME === undefined)
        return null;

    const varianceTarget = payload.virtualMME ?? payload.mme;
    const color = getVarianceColor(varianceTarget, payload.actualMME);

    return (
        <circle
            cx={cx}
            cy={cy}
            r={6}
            fill={color}
            stroke={color}
            strokeWidth={2}
        />
    );
};

export const CustomTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
    const { t } = useTranslation();
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        const varianceTarget = data.virtualMME ?? data.mme;
        const actualMMEColor = data.actualMME !== undefined
            ? getVarianceColor(varianceTarget, data.actualMME)
            : '#2f9e44';

        return (
            <Paper p="xs" shadow="md" style={{ border: '1px solid #1C7ED6' }}>
                <Stack gap="xxs">
                    <Text size="sm" fw={600}>{data.dateLabel}</Text>
                    <Text size="sm" c="#1C7ED6">{t('otp.graph.legend.targetMME')}: {data.mme}</Text>
                    {data.virtualMME !== undefined && (
                        <Text size="sm" c="#9c27b0">{t('otp.graph.legend.virtualMME', 'Virtual MME')}: {data.virtualMME}</Text>
                    )}
                    {data.actualMME !== undefined && (
                        <Text size="sm" c={actualMMEColor}>{t('otp.graph.legend.actualMME')}: {data.actualMME}</Text>
                    )}
                    <Text size="xs" c="dimmed">{data.percentage}{t('otp.stepsTable.percentOfOriginal')}</Text>
                    <Text size="xs" c="dimmed">{t('otp.stepsTable.step')} {data.step}</Text>
                </Stack>
            </Paper>
        );
    }
    return null;
};

/**
 * Hook that returns a getStatusLabel function bound to the current translation context.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useGetStatusLabel = () => {
    const { t } = useTranslation();

    return (status: OTPStatus): string => {
        switch (status) {
            case OTPStatus.Draft: return t('otp.planManager.status.draft', 'Draft');
            case OTPStatus.PendingValidation: return t('otp.planManager.status.pendingValidation', 'Pending Validation');
            case OTPStatus.PendingHcpValidation: return t('otp.planManager.status.pendingHcpValidation', 'Pending HCP Validation');
            case OTPStatus.Active: return t('otp.planManager.status.active', 'Active');
            case OTPStatus.Completed: return t('otp.planManager.status.completed', 'Completed');
            case OTPStatus.Aborted: return t('otp.planManager.status.aborted', 'Aborted');
            case OTPStatus.Appended: return t('otp.planManager.status.appended', 'Appended');
            default: return t('otp.planManager.status.unknown', 'Unknown');
        }
    };
};
